import * as React from 'react';

export type LoadingStateHookResult = [
    boolean,
    <T>(fn: Promise<T> | (() => T | Promise<T>)) => Promise<T>,
    React.Dispatch<React.SetStateAction<boolean>>,
];

/**
 * React hook to create a loading state.
 */
export function useLoadingState(defaultValue = false): LoadingStateHookResult {
    const [loading, setLoading] = React.useState<boolean>(defaultValue);
    const mountedRef = React.useRef<boolean>(false);
    const wrapperIdRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const wrapLoading = React.useCallback(async <T>(fn: Promise<T> | (() => T | Promise<T>)) => {
        const id = wrapperIdRef.current && mountedRef.current ? null : Math.random();
        if (id) {
            wrapperIdRef.current = id;
            setLoading(true);
        }

        try {
            const result = fn instanceof Promise ? await fn : await fn();
            return result;
        } finally {
            if (id === wrapperIdRef.current) {
                wrapperIdRef.current = null;
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        }
    }, []);

    return React.useMemo(() => [loading, wrapLoading, setLoading], [loading, wrapLoading]);
}
